// =============================================================================
// api.js — cliente HTTP único do Fênix.
//
// A API roda FORA da rede do Centrão (VPS da Apps, junto do MySQL). É isso que
// mantém o sistema de pé quando o 192.168.0.55 cai — ver docs/adr/0001.
// =============================================================================

import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5015',
});

// Chave própria por sistema — o parque inteiro compartilha o navegador do
// usuário, e 'token' genérico faz um sistema derrubar a sessão do outro.
export const CHAVE_TOKEN = 'fnx_token';

// O crachá do vendedor — prova assinada de que a senha dele foi conferida.
// Vive só na memória da aba: não vai para o localStorage de propósito, porque
// "de quem é a venda" não pode sobreviver a um F5 nem a uma aba esquecida.
let crachaVendedor = null;
export const setCracha = (v) => { crachaVendedor = v; if (!v) esquecerCrachaDev(); };

// ⚠️ SÓ NO DEV (npm start). Na homologação o vendedor digita a senha uma vez e a
//    aba lembra o crachá (sessionStorage) até ele vencer — sem isso, cada volta à
//    tela de orçamento pedia a senha de novo e travava o teste. O build de
//    produção remove este código (NODE_ENV === 'production'): lá a senha
//    continua sendo pedida a cada orçamento, como decidido.
const CHAVE_DEV = 'fenix.dev.cracha';
const DEV = process.env.NODE_ENV === 'development';
export function lembrarCrachaDev(vendedor, cracha) {
  if (!DEV) return;
  try { sessionStorage.setItem(CHAVE_DEV, JSON.stringify({ vendedor, cracha })); } catch { /* sem storage, pede a senha */ }
}
export function esquecerCrachaDev() {
  if (!DEV) return;
  try { sessionStorage.removeItem(CHAVE_DEV); } catch { /* nada */ }
}
export function crachaDevLembrado() {
  if (!DEV) return null;
  try {
    const salvo = JSON.parse(sessionStorage.getItem(CHAVE_DEV) || 'null');
    if (!salvo?.cracha || !salvo?.vendedor) return null;
    const exp = JSON.parse(atob(salvo.cracha.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp;
    if (!exp || exp * 1000 < Date.now() + 60000) { esquecerCrachaDev(); return null; }
    return salvo;
  } catch { return null; }
}
export const getCracha = () => crachaVendedor;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(CHAVE_TOKEN);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (crachaVendedor) config.headers['x-cracha-vendedor'] = crachaVendedor;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (erro) => {
    // ⚠️ SÓ DERRUBA A SESSÃO QUANDO O SERVIDOR DIZ QUE É A SESSÃO.
    //
    //    Antes bastava um 401 qualquer, e isso quebrou a venda: a senha do
    //    vendedor errada respondia 401, o interceptor apagava o token e jogava
    //    a pessoa no login no meio do orçamento — como se ela tivesse sido
    //    desconectada. Um errinho de digitação custava a sessão inteira.
    //
    //    Agora quem decide é o backend, que marca `sessao: 'invalida'` apenas
    //    no que é de fato token ausente, expirado ou inválido.
    if (erro.response?.status === 401 && erro.response?.data?.sessao === 'invalida') {
      localStorage.removeItem(CHAVE_TOKEN);
      if (!window.location.pathname.startsWith('/login')) window.location.href = '/login';
    }
    return Promise.reject(erro);
  }
);

// Extrai a mensagem que o backend já escreveu em português. O backend nunca
// devolve "erro interno" genérico — se chegou aqui sem mensagem, é falha de
// rede, e aí a frase certa é outra.
//
// ⚠️ NÃO AFIRME QUE É FALTA DE INTERNET. O navegador não conta ao JavaScript
//    quando bloqueou por CORS: chega como ERR_NETWORK, igualzinho a cabo
//    desconectado. Uma tela dizendo "verifique a internet" enquanto o servidor
//    está no ar manda a pessoa procurar no lugar errado — aconteceu em
//    21/08/2026, com o front na 3016 e o CORS liberando só a 3015.
export function mensagemDeErro(erro, padrao = 'Não foi possível concluir a operação.') {
  if (erro?.response?.data?.erro) return erro.response.data.erro;
  if (erro?.code === 'ERR_NETWORK') {
    const alvo = erro?.config?.baseURL || 'o servidor';
    return `Não foi possível falar com ${alvo}. `
      + 'Pode ser a conexão do aparelho, o servidor fora do ar, ou a origem desta tela não estar liberada no CORS.';
  }
  if (erro?.code === 'ECONNABORTED') {
    return 'O servidor demorou demais para responder. Tente de novo.';
  }
  return padrao;
}

export default api;
